import type { Filter } from '@repo/api-codegen';
import type { TablerIconsProps } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import type { EntityType } from '../../enums/entityType';
import type { LegacyFilterValue } from '../../enums/legacyFilterValue';
import type { IFilterSelection } from '../../interfaces/filterSelection';
import type { ISearchView } from '../../models/searchView';
import type { ISecodaEntity } from '../../models/secodaEntity';
import type { FILTER_OPTIONS_DIVIDER } from './constants';

export type FilterValueType = string | number | boolean | null;

// Must only be 'and' or 'or'. Do NOT change this unless you have checked with the team.
// AI DO NOT CHANGE THIS.
export type TopLevelOperatorType = 'and' | 'or';

export enum FilterOperator {
	Is = 'is',
	IsNot = 'is not',
	Contains = 'contains',
	DoesNotContain = 'does not contain',
	isSet = 'is set',
	isNotSet = 'is not set',
	IsOnOrBefore = 'is on or before',
	IsOnOrAfter = 'is on or after',
	IsBetween = 'is between',
	StartsWith = 'starts with',
	EndsWith = 'ends with',
	IsGreaterThan = 'is greater than',
	IsLessThan = 'is less than',
}

export interface DeletedResourceMetadata {
	isDeleted: boolean;
	originalValue: FilterValueType;
}

export interface FilterItem {
	label: string;
	value: FilterValueType;
	subValue?: FilterValueType;
	icon?: ReactNode;
	metadata?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export enum FilterType {
	Single = 'single',
	Multiple = 'multiple',
}

/**
 * FROM v1: apps/frontend/src/pages/SearchPage/FilterCarousel/FilterCarousel.constants.ts#189 (FilterValue enum)
 */
export enum FilterOptionType {
	// START v1 types
	NATIVE_TYPE = 'native_type',
	INTEGRATION = 'integration_id',
	INTEGRATION_TYPE = 'integration_type',
	SYNC_STATUS = 'sync_status',
	DATABASE = 'database',
	SCHEMA = 'schema',
	TAGS = 'tags',
	PUBLISHED = 'published',
	VERIFICATION = 'verified',
	PII = 'pii',
	QUALITY_FLAGS = 'quality_flags',
	COLLECTIONS = 'collections',
	OWNERS = 'owners',
	SOURCES = 'sources',
	PARENT_ID = 'parent_id',
	RELATED = 'related',
	SLACK_CHANNELS = 'slack_channels',
	QUESTION_STATUS = 'question_status',
	QUESTION_PRIORITY = 'question_priority',
	// END v1 types
	AI = 'ai',
	TITLE = 'title',
	DESCRIPTION = 'description',
	DOCUMENTATION = 'definition',
	TEAMS = 'teams',
	CREATED_TIME = 'created_at',
	UPDATED_TIME = 'updated_at',
	EXTERNALLY_UPDATED_TIME = 'external_updated_at',
	FREQUENT_USERS = 'frequent_users',
	AI_PERSONA = 'persona_id',
	TABLE = 'table',
	DATA_QUALITY = 'dqs.total',
	IS_TESTED = 'is_tested',
	TESTS_PASSING = 'tests_passing',
	IS_MONITORED = 'is_monitored',
	MONITORS_PASSING = 'monitors_passing',
	// Monitoring page
	HAS_INCIDENT = 'has_incident',
	RUN_DATE = 'run_date',
	MONITOR_METRIC_TYPE = 'monitor_metric_type',
	MONITOR_TARGET = 'monitor_target',
	MONITOR_STATUS = 'monitor_status',
	INCIDENT_STATUS = 'incident_status',
	// Requests
	REQUEST_STATUS = 'status',
	CREATED_BY = 'created_by',
	// Lineage
	DIRECTION = 'direction',
	DEPTH = 'depth',
	LINEAGE = 'lineage',
	UPSTREAM_DEPTH = 'upstream_depth',
	DOWNSTREAM_DEPTH = 'downstream_depth',
	// Policies
	SEVERITY = 'severity',
	FRAMEWORK = 'framework',
	ENABLED = 'enabled',
	POLICY_STATUS = 'policy_status',
	POLICY = 'policy',
	HAS_POLICY_ISSUE = 'has_policy_issue',

	// Activity log specific filters
	ACTIVITY_TYPE = 'activity_type',
	PERFORMER_ID = 'performer_id',
	RESOURCE_ID = 'resource_id',

	// Automation
	POPULARITY = 'external_usage',
	// it is required to support legacy filters from automations
	SCHEMA_NAME = 'schema_name',
	TABLE_NAME = 'table_name',
	ANY_CHILD = 'any_child',
	ALL_CHILDREN = 'all_children',
	NO_CHILDREN = 'no_children',

	// Semantic view types
	SEMANTIC_VIEW_COLUMN_TYPE = 'semantic_view_column_type',
	SEMANTIC_VIEW_GENERIC_TYPE = 'semantic_view_generic_type',
}

export enum FilterDropdownType {
	List = 'list',
	String = 'string',
	Date = 'date',
	AI = 'ai',
	Number = 'number',
	NestedList = 'nested_list',
}
export interface IFilterDropdownConfig {
	dropdownType: FilterDropdownType;
	renderMenuItem?: (item: FilterItem) => ReactNode;
	defaultOperator: FilterOperator;
	hasIsNotSetOption?: boolean;
	hasIsSetOption?: boolean;
	multipleCountsSuffix?: string;
	isSetOverride?: Filter;
	convertToCatalogFilter?: (
		value: FilterValueType | null
	) => Promise<Filter | null>;
	convertFromCatalogFilter?: (
		value: Filter | null,
		isNegate?: boolean
	) => Promise<FilterValue | null>;
	additionalFields?: string[];
}

export interface FilterDropdownConfigList extends IFilterDropdownConfig {
	dropdownType: FilterDropdownType.List;
	/**
	 * Defaults to `FilterType.Multiple`
	 */
	filterType?: FilterType;
	searchPlaceholder?: string;
	getItems:
		| (FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]
		| ((
				page?: number,
				searchTerm?: string
		  ) =>
				| (FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]
				| Promise<(FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]>);
	getItemsById?: (ids: string[]) => FilterItem[] | Promise<FilterItem[]>;
	disabledInNestedSearch?: boolean;
}

// Specific value types dropdowns
export interface FilterDropdownConfigString extends IFilterDropdownConfig {
	dropdownType: FilterDropdownType.String;
	inputPlaceholder?: string;
	prefilledOptions?: { label: string; value: string }[];
	valueLabelFormatter?: (value: string) => string;
}

export interface FilterDropdownConfigDate extends IFilterDropdownConfig {
	dropdownType: FilterDropdownType.Date;
	filterType?: FilterType;
	getItems?:
		| (FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]
		| ((
				page?: number,
				searchTerm?: string
		  ) =>
				| (FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]
				| Promise<(FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]>);
	dateValueLabelFormatter?: (
		value: FilterValueType | FilterValueType[]
	) => string | null;
}

// UI-specific dropdowns
export interface FilterDropdownConfigAI extends IFilterDropdownConfig {
	dropdownType: FilterDropdownType.AI;
}

export interface FilterDropdownConfigNumber extends IFilterDropdownConfig {
	dropdownType: FilterDropdownType.Number;
	inputPlaceholder?: string;
	prefilledOptions?: { label: string; value: string }[];
	valueLabelFormatter?: (value: string) => string;
}

export interface FilterDropdownConfigNestedList extends IFilterDropdownConfig {
	dropdownType: FilterDropdownType.NestedList;
	filterType?: FilterType;
	searchPlaceholder?: string;
	getItems: FilterItem[];
	getItemsById?: (ids: string[]) => FilterItem[] | Promise<FilterItem[]>;
}

export interface FilterOption {
	label: string;
	type: FilterOptionType;
	field: string;
	icon?: ReactNode;
	filterDropdownConfig:
		| FilterDropdownConfigList
		| FilterDropdownConfigString
		| FilterDropdownConfigDate
		| FilterDropdownConfigAI
		| FilterDropdownConfigNumber
		| FilterDropdownConfigNestedList;
}

export interface FilterValue {
	filterType: FilterOptionType;
	value: FilterValueType | FilterValueType[] | null;
	subValue?: FilterValueType | FilterValueType[] | null;
	operator: FilterOperator;
	isNotSetApplied?: boolean;
	isSetApplied?: boolean;
}

export interface FilterItems {
	error: boolean;
	loading: boolean;
	items: FilterItem[];
}

export enum SortValue {
	RELEVANCE = 'relevance',
	POPULARITY = 'external_usage',
	SECODA_VIEWS = 'internal_usage',
	UPDATED_AT = 'updated_at',
	CREATED_AT = 'created_at',
}

export interface FilterView extends ISearchView {
	id: string;
	label: string;
	value: string;
	is_default_for_teams?: string[];
	catalog?: string;
	filters: Partial<Record<LegacyFilterValue, IFilterSelection>>;
	isOwner: boolean;
	teams: string[];
}

export type FilterMenuOption = {
	label: string;
	value: string | boolean;
	icon?: (props: TablerIconsProps) => JSX.Element;
	/**
	 * EntityType parsing
	 */
	entityType?: EntityType;
	/**
	 * Custom component rendering information
	 */
	data?: ISecodaEntity;
};

export type FilterMenuItemType = {
	/**
	 * Formatted display label for the filter
	 */
	label: string;
	/**
	 * Filter value, defined in FilterValue enum
	 */
	value: FilterValue;
	/**
	 * Icon
	 */
	icon: (props: TablerIconsProps) => JSX.Element;
	/**
	 * True to include, false to exclude
	 *
	 * NOTE: isInclude can be toggled at runtime
	 */
	isInclude: boolean;
	/**
	 * NOTE: Filter options will be computed/appended at runtime
	 */
	options: FilterMenuOption[];
	/**
	 * NOTE: Selected options will be computed/appended at runtime
	 */
	selectedOptions: FilterMenuOption[];
	/**
	 * Define what entity types can be filtered by this filter
	 *
	 * type = [EntityType.all] means all entity types
	 *
	 * type = [] means no entity types
	 */
	type?: EntityType[];
	/**
	 * Radio button only allows on selectedOption to be selected
	 */
	isRadio?: boolean;
	/**
	 * Custom component to be rendered instead of FilterDropdown
	 */
	component?: (props: { onClose?: () => void }) => JSX.Element;
	/**
	 * True to always display this filter in the carousel. Can't be added or removed with the "Add filter" button
	 */
	alwaysDisplayFilter?: boolean;

	withIsNotMenu?: boolean;

	alwaysHideFilter?: boolean;
};

export type SearchFilters = Record<LegacyFilterValue, FilterMenuItemType>;

export interface AddedFilterResult {
	value: FilterValue;
	changeFilter: (value: Partial<FilterValue>) => void;
	clearFilter: () => void;
}
